import { getActiveVat } from "../api/vat.api";

// NOTE: createContext, useState, useEffect, useCallback, useMemo are auto-imported by unplugin-auto-import

const VatContext = createContext(null);

export const VatProvider = ({ children }) => {
    const [vatRate, setVatRate] = useState(16); // default Kenya VAT
    const [vatLoading, setVatLoading] = useState(true);

    const fetchVat = useCallback(async () => {
        try {
            setVatLoading(true);
            const response = await getActiveVat();
            if (response.data?.isOk && response.data?.data?.percentage != null) {
                setVatRate(response.data.data.percentage);
            }
        } catch {
            // Keep the fallback default
            console.error("Failed to fetch VAT rate, using default 16%");
        } finally {
            setVatLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchVat();
    }, [fetchVat]);

    /**
     * Calculate VAT amount from a subtotal
     * @param {number} subtotal
     * @returns {number}
     */
    const calculateVat = useCallback(
        (subtotal) => {
            return Math.round(((subtotal * vatRate) / 100) * 100) / 100;
        },
        [vatRate]
    );

    /**
     * Calculate total including VAT
     * @param {number} subtotal
     * @returns {{ vatAmount: number, total: number }}
     */
    const calculateWithVat = useCallback(
        (subtotal) => {
            const vatAmount = calculateVat(subtotal);
            return {
                vatAmount,
                total: Math.round((subtotal + vatAmount) * 100) / 100,
            };
        },
        [calculateVat]
    );

    const value = useMemo(
        () => ({
            vatRate,
            vatLoading,
            calculateVat,
            calculateWithVat,
            refreshVat: fetchVat,
        }),
        [vatRate, vatLoading, calculateVat, calculateWithVat, fetchVat]
    );

    return (
        <VatContext.Provider value={value}>
            {children}
        </VatContext.Provider>
    );
};

export const useVatContext = () => {
    const context = useContext(VatContext);
    if (!context) {
        throw new Error("useVatContext must be used within a VatProvider");
    }
    return context;
};

export { VatContext };
export default VatContext;
